// frontend/src/context/ChatbotScopeContext.tsx
// <purpose>Site-wide Kundschaft⇄Test view scope for chatbot analytics. Persisted in localStorage.</purpose>
// <constraints>FE-only: key 'stx-chatbot-scope'. Default 'kundschaft'. No backend.</constraints>
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type ChatbotScope = 'kundschaft' | 'test';
const KEY = 'stx-chatbot-scope';

function readStored(): ChatbotScope {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'test' ? 'test' : 'kundschaft';
  } catch { return 'kundschaft'; }
}

interface Ctx { scope: ChatbotScope; setScope: (s: ChatbotScope) => void; toggle: () => void; }
const ChatbotScopeCtx = createContext<Ctx>({ scope: 'kundschaft', setScope: () => {}, toggle: () => {} });

export const ChatbotScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scope, setScopeState] = useState<ChatbotScope>(readStored);
  const setScope = useCallback((s: ChatbotScope) => {
    setScopeState(s);
    try { window.localStorage.setItem(KEY, s); } catch { /* private-mode etc. */ }
  }, []);
  const toggle = useCallback(() => setScope(readStored() === 'test' ? 'kundschaft' : 'test'), [setScope]);
  const value = useMemo(() => ({ scope, setScope, toggle }), [scope, setScope, toggle]);
  return <ChatbotScopeCtx.Provider value={value}>{children}</ChatbotScopeCtx.Provider>;
};

export function useChatbotScope(): Ctx { return useContext(ChatbotScopeCtx); }
